import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCzk, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, ShieldCheck, MapPin, Wallet, Search, Star, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const copyText = async (text, label = "Zkopírováno") => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error("Kopírování selhalo");
  }
};

/** Full provider directory tab for admin */
export const ProvidersDirectory = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all, verified, unpaid

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/providers");
      setProviders(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const visible = providers.filter((p) => {
    if (filter === "verified" && !p.verified) return false;
    if (filter === "unpaid" && p.paid_unsettled <= 0) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (p.business_name || "").toLowerCase().includes(needle) ||
      (p.name || "").toLowerCase().includes(needle) ||
      (p.email || "").toLowerCase().includes(needle) ||
      (p.city || "").toLowerCase().includes(needle) ||
      (p.iban || "").toLowerCase().includes(needle)
    );
  });

  if (loading) return <Card className="p-10 text-center text-slate-500">Načítání…</Card>;

  return (
    <div className="space-y-4">
      <Card className="p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat: jméno, město, email, č. účtu…" className="pl-9" data-testid="admin-providers-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="admin-providers-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všechny ({providers.length})</SelectItem>
            <SelectItem value="verified">Pouze ověřené</SelectItem>
            <SelectItem value="unpaid">S neuhrazenou výplatou</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {visible.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">Žádné prádelny pro tento filtr.</Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {visible.map((p) => (
            <ProviderRow key={p.id} provider={p} onChange={reload} />
          ))}
        </div>
      )}
    </div>
  );
};

const ProviderRow = ({ provider: p, onChange }) => {
  return (
    <Card className="p-5" data-testid={`admin-provider-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 text-white grid place-items-center font-display font-bold text-lg flex-shrink-0">
            {p.business_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Link to={`/admin/uzivatel/${p.user_id}`} className="font-display font-bold text-slate-900 hover:text-sky-600 truncate" data-testid={`admin-provider-name-${p.id}`}>
                {p.business_name}
              </Link>
              {p.verified ? (
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-[10px]">✓ Ověřeno</Badge>
              ) : p.verification_status === "pending" ? (
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px]">Čeká</Badge>
              ) : p.verification_status === "rejected" ? (
                <Badge variant="secondary" className="bg-rose-100 text-rose-800 text-[10px]">Zamítnuto</Badge>
              ) : null}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{p.name} · {p.email}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {p.city || "—"} {p.zip}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {p.rating_count > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
              <span className="font-semibold">{p.rating_avg}</span>
              <span className="text-slate-400">({p.rating_count})</span>
            </div>
          )}
          <div className="text-[10px] text-slate-400">{p.total_orders} zakázek</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2">
          <div className="text-[10px] text-emerald-700 uppercase tracking-wider">Vyplaceno</div>
          <div className="font-display font-bold text-emerald-900">{formatCzk(p.paid_settled)}</div>
        </div>
        <div className={`rounded-lg p-2 ${p.paid_unsettled > 0 ? "bg-rose-50 border border-rose-200" : "bg-slate-50 border border-slate-100"}`}>
          <div className={`text-[10px] uppercase tracking-wider ${p.paid_unsettled > 0 ? "text-rose-700" : "text-slate-500"}`}>K vyplacení</div>
          <div className={`font-display font-bold ${p.paid_unsettled > 0 ? "text-rose-900" : "text-slate-700"}`}>{formatCzk(p.paid_unsettled)}</div>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-2">
          <div className="text-[10px] text-amber-700 uppercase tracking-wider">V běhu</div>
          <div className="font-display font-bold text-amber-900">{formatCzk(p.pending)}</div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Číslo účtu pro výplatu</div>
          {p.iban ? (
            <div className="flex items-center gap-1">
              <code className="text-xs text-slate-700 truncate" data-testid={`admin-provider-iban-${p.id}`}>{p.iban}</code>
              <button onClick={() => copyText(p.iban, "Číslo účtu zkopírováno")} className="text-slate-400 hover:text-sky-600" data-testid={`admin-provider-iban-copy-${p.id}`}>
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="text-xs text-rose-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Není zadán (neověřeno)</div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {p.paid_unsettled > 0 && (
            <PayoutDialog provider={p} onChange={onChange} />
          )}
          <Link to={`/admin/uzivatel/${p.user_id}`} className="text-sm text-sky-600 hover:underline font-semibold" data-testid={`admin-provider-detail-${p.id}`}>
            Detail →
          </Link>
        </div>
      </div>
    </Card>
  );
};

const PayoutDialog = ({ provider, onChange }) => {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [method, setMethod] = useState("bank_transfer");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/providers/${provider.user_id}/unpaid-orders`);
      setOrders(data);
      setSelected(new Set(data.map((o) => o.id)));
    } finally { setLoading(false); }
  };

  const onOpen = (v) => { setOpen(v); if (v) load(); };

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const total = orders.filter((o) => selected.has(o.id)).reduce((s, o) => s + (o.provider_payout || 0), 0);

  const submit = async () => {
    if (selected.size === 0) { toast.error("Vyberte alespoň jednu objednávku"); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-mark-paid", {
        order_ids: [...selected], method, note,
      });
      toast.success(`Označeno jako vyplaceno: ${data.marked} objednávek`);
      setOpen(false);
      onChange?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setPaying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <Button size="sm" onClick={() => onOpen(true)} data-testid={`admin-payout-btn-${provider.id}`}>
        <Wallet className="w-4 h-4 mr-1.5" /> Vyplatit
      </Button>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Vyplacení odměny — {provider.business_name}</DialogTitle>
        </DialogHeader>

        {provider.iban && (
          <div className="rounded-lg bg-sky-50 border border-sky-200 p-3">
            <div className="text-[10px] text-sky-700 font-semibold uppercase tracking-wider">Bankovní spojení</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-sm flex-1">{provider.iban}</code>
              <button onClick={() => copyText(provider.iban, "Číslo účtu zkopírováno")} className="text-sky-700 hover:text-sky-900" data-testid="payout-iban-copy">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {provider.full_legal_name && (
              <div className="text-xs text-sky-700 mt-1">Jméno na účtu: {provider.full_legal_name}</div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500 p-4 text-center">Načítání…</div>
        ) : orders.length === 0 ? (
          <div className="text-sm text-slate-500 p-4 text-center">Žádné neuhrazené objednávky.</div>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Zákazník</TableHead>
                  <TableHead className="text-right">Odměna</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => toggle(o.id)} data-testid={`payout-order-${o.id}`}>
                    <TableCell>
                      <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} onClick={(e) => e.stopPropagation()} data-testid={`payout-checkbox-${o.id}`} />
                    </TableCell>
                    <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString("cs-CZ")}</TableCell>
                    <TableCell className="text-sm">{o.customer_name}</TableCell>
                    <TableCell className="text-right font-display font-bold text-emerald-700">{formatCzk(o.provider_payout)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Způsob platby</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="payout-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bankovní převod</SelectItem>
                <SelectItem value="cash">Hotově</SelectItem>
                <SelectItem value="other">Jiné</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">K vyplacení celkem</div>
            <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="payout-total">{formatCzk(total)}</div>
            <div className="text-[10px] text-slate-400">{selected.size} z {orders.length} objednávek</div>
          </div>
        </div>
        <div>
          <Label>Poznámka (např. číslo transakce)</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="payout-note" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="payout-cancel">Zrušit</Button>
          <Button onClick={submit} disabled={paying || selected.size === 0} data-testid="payout-confirm">
            {paying ? "Ukládám…" : `Potvrdit výplatu ${formatCzk(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { PayoutDialog };
